import { useMemo } from "react";
import { useSelector } from "react-redux";

// F4 · Hook central de permisos. Reglas:
// 1) Si NO hay `info` en employeeSession (nadie fichado con PIN) →
//    el admin del restaurante está usando el TPV directamente → PASA
//    (mismo criterio que `[RequireEmployeeAction]` con
//    `AllowRestaurantOwner=true` en backend).
// 2) Si hay `info` → chequea que la action esté en `info.actions[]`.
// 3) `MaxDiscountPct` / `MaxRefundAmount` se exponen aparte para que
//    quien pinta la UI de descuentos o reembolsos pueda validar el
//    valor concreto (0 = sin permiso; null/undefined = sin límite).
//
// Uso:
//   const canDeletePayed = useCanDo("ORDER_DELETE_LINE");
//   const { can, maxDiscountPct } = useCanDo();
//   if (!can("ORDER_MODIFY_PRICE")) return null;

export default function useCanDo(action) {
  const info = useSelector((s) => s.employeeSession?.info);

  const has = useMemo(() => {
    // Sin sesión de empleado → admin dueño → pasa todo.
    if (!info) return () => true;
    // 2026-08-30 · Empleado SIN rol asignado (RoleId=null) — pasa todo
    // (comportamiento legacy pre-roles). El owner aún no le puso rol
    // formal; hasta que lo haga, no bloqueamos su operativa. Coincide
    // con el backend en `RequireEmployeeActionAttribute`.
    if (!info.roleId) return () => true;
    const set = new Set(info.actions || []);
    return (a) => (a ? set.has(a) : true);
  }, [info]);

  const api = useMemo(
    () => ({
      can: has,
      hasSession: Boolean(info),
      employeeId: info?.employeeId ?? null,
      employeeName: info?.name ?? null,
      roleName: info?.roleName ?? null,
      isSystemRole: info?.isSystemRole ?? false,
      maxDiscountPct: info?.maxDiscountPct ?? null,
      maxRefundAmount: info?.maxRefundAmount ?? null,
    }),
    [info, has],
  );

  if (typeof action === "string") return has(action);
  return api;
}
